import { AI_QUOTA_LOW_RATIO } from 'src/redux/aiNarrative/constants';
import {
  AiNarrativeContext,
  AiSourceReference,
} from 'src/redux/aiNarrative/types';
import { AiQuota, AiQuotaStatus } from 'src/redux/aiQuota/types';
import type { IReportSection, ISectionData } from 'src/types';
import { getPlainText } from 'src/utils';

const PLACEHOLDER_PATTERN = /\{\{\s*(\w+)\s*\}\}|\{\s*(\w+)\s*\}/g;

export function fillTemplate(
  template: string,
  vars: Record<string, string | number>,
): string {
  const missing: string[] = [];

  const filled = template.replace(
    PLACEHOLDER_PATTERN,
    (match, doubled, single) => {
      const name = doubled || single;
      const value = vars[name];

      if (value === undefined || value === null) {
        missing.push(name);
        return match;
      }

      return String(value);
    },
  );

  if (missing.length) {
    throw new Error(
      `Prompt template has unresolved placeholders: ${Array.from(
        new Set(missing),
      ).join(', ')}`,
    );
  }

  return filled;
}

export function quotaStatus(quota: AiQuota | null): AiQuotaStatus {
  if (!quota) {
    return { remaining: Infinity, exhausted: false, low: false };
  }

  const remaining = quota.limit - quota.used;
  const lowFrom = Math.ceil(quota.limit * AI_QUOTA_LOW_RATIO);

  return {
    remaining,
    exhausted: remaining <= 0,
    low: remaining > 0 && remaining <= lowFrom,
  };
}

function findSectionPath(
  sections: IReportSection[],
  activeSectionId: number,
  trail: IReportSection[] = [],
): IReportSection[] | null {
  for (const section of sections || []) {
    const path = [...trail, section];
    if (section.section_pk === activeSectionId) {
      return path;
    }
    const found = section.children_list
      ? findSectionPath(section.children_list, activeSectionId, path)
      : null;
    if (found) {
      return found;
    }
  }
  return null;
}

export function readSectionNames(
  sections: IReportSection[],
  activeSectionId: number,
): { sectionName: string; subsectionName: string } {
  const path = findSectionPath(sections, activeSectionId) || [];
  return {
    sectionName: path[0]?.current_title || '',
    subsectionName:
      path.length > 1 ? path[path.length - 1].current_title || '' : '',
  };
}

function clean(value: unknown): unknown {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }

  if (Array.isArray(value)) {
    const cleaned = value.map(clean).filter(item => item !== undefined);
    return cleaned.length > 0 ? cleaned : undefined;
  }

  if (typeof value === 'object' && value.constructor === Object) {
    const cleaned: Record<string, unknown> = {};
    let hasKeys = false;

    Object.entries(value).forEach(([key, entry]) => {
      const cleanedEntry = clean(entry);
      if (cleanedEntry !== undefined) {
        cleaned[key] = cleanedEntry;
        hasKeys = true;
      }
    });

    return hasKeys ? cleaned : undefined;
  }

  return value;
}

export function sanitizeAndMinifyJson(data: unknown): string {
  return JSON.stringify(clean(data) ?? {});
}

export function buildSourceData(
  section: ISectionData,
  dataKeyMap: Record<string, string | string[]>,
): string {
  if (!section) {
    return '';
  }

  const datasets: Record<string, unknown> = {};
  (section.data_sources || []).forEach(({ id, title }) => {
    const slug = dataKeyMap[id];
    if (!slug) {
      return;
    }
    const value = Array.isArray(slug)
      ? slug.reduce(
          (acc, key) => ({ ...acc, [key]: section.datasets?.[key] }),
          {} as Record<string, unknown>,
        )
      : section.datasets?.[slug];

    if (value !== undefined && value !== null) {
      datasets[title || id] = value;
    }
  });

  return sanitizeAndMinifyJson(datasets);
}

export function promptVariables(
  context: AiNarrativeContext,
  extras: { currentContent?: string; selectedText?: string } = {},
): Record<string, string | number> {
  const plainContent = getPlainText(extras.currentContent || '');

  const variables: Record<string, string | number> = {
    narrative_content: plainContent,
    current_content: plainContent,
    subsection_name: context.subsectionName || '',
    source_data: context.sourceData || '',
  };

  const optional: Record<string, string | number | undefined> = {
    csp_code: context.cspCode,
    country_code: context.countryCode,
    country_name: context.countryName,
    year: context.year,
    section_name: context.sectionName,
    narrative_name: context.narrativeName,
    word_count_limit: context.wordCountLimit,
    narrative_type: context.narrativeType,
  };

  Object.entries(optional).forEach(([name, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      variables[name] = value;
    }
  });

  if (extras.selectedText !== undefined) {
    variables.text = extras.selectedText;
    variables.selected_text = extras.selectedText;
  }

  return variables;
}

const USAGE_ORDER: Record<string, number> = { content: 0, style: 1 };

export function sortSourceReferences(
  references: AiSourceReference[],
): AiSourceReference[] {
  return [...(references || [])].sort((a, b) => {
    const rank = (r: AiSourceReference) => USAGE_ORDER[r.usage ?? ''] ?? 2;
    const byUsage = rank(a) - rank(b);
    return byUsage !== 0 ? byUsage : sourceNumber(a) - sourceNumber(b);
  });
}

export function sourceNumber(reference: AiSourceReference): number {
  const digits = (reference.source_id || '').replace(/\D/g, '');
  return digits ? Number(digits) : Number.MAX_SAFE_INTEGER;
}

// The backend numbers every document it was given, including the ones dropped
// as not used, so the listed sources are renumbered 1..n in display order.
export function numberSources(sortedReferences: AiSourceReference[]): {
  byId: Map<string, number>;
  bySourceNumber: Map<number, number>;
} {
  const byId = new Map<string, number>();
  const bySourceNumber = new Map<number, number>();
  sortedReferences.forEach((reference, index) => {
    byId.set(reference.id, index + 1);
    const original = sourceNumber(reference);
    if (original !== Number.MAX_SAFE_INTEGER && !bySourceNumber.has(original)) {
      bySourceNumber.set(original, index + 1);
    }
  });
  return { byId, bySourceNumber };
}
